/**
 * Markdown rendering for AI Workspace assistant answers.
 *
 * Assistant output is model-generated text. It is not user input, but it is not
 * trusted either — a Skill body is admin-authored and the model can be steered by
 * what a user pastes into a message. Everything rendered as HTML goes through
 * DOMPurify; nothing reaches v-html without passing through render() below.
 *
 * User messages are NOT rendered as markdown. They are shown as plain text via
 * mustache interpolation, so they need no sanitising and cannot be styled by
 * anything a user types.
 */

import { marked } from "marked";
import DOMPurify from "dompurify";

marked.setOptions({
  gfm: true,
  // Single newlines become <br>. Chat answers are written conversationally and
  // rarely use the two-space line-break convention.
  breaks: true,
  // Heading ids would collide across messages in one transcript, and nothing
  // links to them.
  headerIds: false,
  mangle: false,
});

// Links open in a new tab. Without noopener the opened page gets a handle on
// window.opener and can navigate ARCH somewhere else.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "hr",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "strong", "em", "del", "blockquote",
    "ul", "ol", "li",
    "code", "pre",
    "table", "thead", "tbody", "tr", "th", "td",
    "a",
  ],
  ALLOWED_ATTR: ["href", "title", "class"],
  // Belt and braces alongside the tag allowlist: these are the tags that turn a
  // rendered answer into script execution or a credential prompt.
  FORBID_TAGS: ["script", "style", "iframe", "form", "input", "object", "embed"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "style"],
  ALLOW_DATA_ATTR: false,
};

/**
 * Render assistant markdown to sanitised HTML.
 *
 * @param {string} text
 * @returns {string} HTML safe to pass to v-html
 */
export function render(text) {
  if (!text) {
    return "";
  }

  try {
    return DOMPurify.sanitize(marked.parse(text), SANITIZE_CONFIG);
  } catch {
    // A malformed answer should still be readable rather than disappearing.
    return DOMPurify.sanitize(`<p>${text}</p>`, SANITIZE_CONFIG);
  }
}

export default { render };
